import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { MapLayoutValidationIssue } from "@/lib/office/map-layout-validate";
import type { MapLayoutDocument } from "@/lib/office/personal-office-layout";
import { mapLayoutKeys, personalOfficeKeys } from "@/lib/query";

export type MapLayoutDto = {
  id: string;
  mapId?: string;
  status: "draft" | "published";
  version: number;
  layout: MapLayoutDocument;
  updatedAt: string | Date;
  updatedBy: string | null;
};

export type LayoutApiError = {
  message: string;
  issues: MapLayoutValidationIssue[];
  ownedSlotKeys: string[];
};

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => null);
}

function mapIdQuery(mapId: string): string {
  return mapId && mapId !== "campus" ? `?mapId=${encodeURIComponent(mapId)}` : "";
}

function coerceIssues(raw: unknown): MapLayoutValidationIssue[] {
  if (!Array.isArray(raw)) return [];
  const out: MapLayoutValidationIssue[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item as {
      path?: unknown;
      message?: unknown;
      selection?: unknown;
    };
    const path = typeof rec.path === "string" ? rec.path : "";
    const message = typeof rec.message === "string" ? rec.message : "";
    if (!message) continue;
    const issue: MapLayoutValidationIssue = { path, message };
    if (rec.selection && typeof rec.selection === "object") {
      const sel = rec.selection as { kind?: unknown; id?: unknown; index?: unknown };
      if (sel.kind === "spawn" && typeof sel.index === "number") {
        issue.selection = { kind: "spawn", index: sel.index };
      } else if (
        (sel.kind === "office" ||
          sel.kind === "zone" ||
          sel.kind === "furniture" ||
          sel.kind === "transition") &&
        typeof sel.id === "string"
      ) {
        issue.selection = { kind: sel.kind, id: sel.id };
      }
    }
    out.push(issue);
  }
  return out;
}

/** Parse API error body into structured issues for the editor panel. */
export function parseLayoutApiError(body: {
  error?: string;
  issues?: unknown;
  ownedSlotKeys?: string[];
}): LayoutApiError {
  const issues = coerceIssues(body.issues);
  const ownedSlotKeys = Array.isArray(body.ownedSlotKeys)
    ? body.ownedSlotKeys.filter((k): k is string => typeof k === "string")
    : [];

  if (ownedSlotKeys.length > 0) {
    issues.push({
      path: "ownedOffices",
      message: `Cannot remove offices that still have owners — unassign them first: ${ownedSlotKeys.join(", ")}`,
    });
  }

  const count = issues.length;
  let message = body.error ?? "Request failed";
  if (count > 0) {
    message =
      count === 1
        ? (issues[0]?.message ?? message)
        : `${count} issues — see the list below the toolbar`;
  }
  return { message, issues, ownedSlotKeys };
}

export function usePublishedMapLayout(mapId: string = "campus") {
  return useQuery({
    queryKey: mapLayoutKeys.published(mapId),
    queryFn: async () => {
      const res = await fetch(`/api/map-layout${mapIdQuery(mapId)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load map layout");
      const body = (await parseJson(res)) as { data?: MapLayoutDto };
      if (!body?.data) throw new Error("Invalid map layout response");
      return body.data;
    },
  });
}

export function useAdminMapLayouts(mapId: string = "campus") {
  return useQuery({
    queryKey: mapLayoutKeys.admin(mapId),
    queryFn: async () => {
      const res = await fetch(`/api/admin/map-layout${mapIdQuery(mapId)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load admin map layout");
      const body = (await parseJson(res)) as {
        data?: { draft: MapLayoutDto; published: MapLayoutDto };
      };
      if (!body?.data) throw new Error("Invalid admin map layout response");
      return body.data;
    },
  });
}

export class MapLayoutMutationError extends Error {
  issues: MapLayoutValidationIssue[];
  ownedSlotKeys: string[];

  constructor(parsed: LayoutApiError) {
    super(parsed.message);
    this.name = "MapLayoutMutationError";
    this.issues = parsed.issues;
    this.ownedSlotKeys = parsed.ownedSlotKeys;
  }
}

export function useSaveMapLayoutDraft(mapId: string = "campus") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (layout: MapLayoutDocument) => {
      const res = await fetch(`/api/admin/map-layout${mapIdQuery(mapId)}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...layout, id: mapId }),
      });
      const body = (await parseJson(res)) as {
        data?: MapLayoutDto;
        error?: string;
        issues?: unknown;
        ownedSlotKeys?: string[];
      };
      if (!res.ok) {
        throw new MapLayoutMutationError(
          parseLayoutApiError({
            error: body?.error ?? "Failed to save draft",
            issues: body?.issues,
            ownedSlotKeys: body?.ownedSlotKeys,
          }),
        );
      }
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mapLayoutKeys.admin(mapId) });
      toast.success("Draft saved");
    },
    onError: (err: Error) => {
      toast.error(err.message, { duration: 6000 });
    },
  });
}

export function usePublishMapLayout(mapId: string = "campus") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (layout?: MapLayoutDocument) => {
      const res = await fetch(`/api/admin/map-layout/publish${mapIdQuery(mapId)}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: layout ? JSON.stringify({ ...layout, id: mapId }) : JSON.stringify({}),
      });
      const body = (await parseJson(res)) as {
        data?: { published: MapLayoutDto; draft: MapLayoutDto };
        error?: string;
        ownedSlotKeys?: string[];
        issues?: unknown;
      };
      if (!res.ok) {
        throw new MapLayoutMutationError(
          parseLayoutApiError({
            error: body?.error ?? "Failed to publish",
            issues: body?.issues,
            ownedSlotKeys: body?.ownedSlotKeys,
          }),
        );
      }
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mapLayoutKeys.all });
      void qc.invalidateQueries({ queryKey: personalOfficeKeys.all });
      toast.success("Map published — live world updated");
    },
    onError: (err: Error) => {
      toast.error(err.message, { duration: 6000 });
    },
  });
}

export function useResetMapLayoutDraft(mapId: string = "campus") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/map-layout/reset-draft${mapIdQuery(mapId)}`, {
        method: "POST",
        credentials: "include",
      });
      const body = (await parseJson(res)) as { data?: MapLayoutDto; error?: string };
      if (!res.ok) throw new Error(body?.error ?? "Failed to reset draft");
      return body.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mapLayoutKeys.admin(mapId) });
      toast.success("Draft reset to published map");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
